import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPendingReviewDocuments } from '@/store/slices/workAuthSlice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Eye, CheckCircle } from 'lucide-react';
import type { DocumentReviewStatus } from '@shared/schemas/workAuth';
import { formatDate, formatDateTime } from '@/utils/dateFormatter';

const WorkAuthReviewPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pendingReviewDocuments, documentsLoading } = useAppSelector(state => state.workAuth);

  useEffect(() => {
    dispatch(fetchPendingReviewDocuments({ status: 'PENDING', page: 1, limit: 50 }));
  }, [dispatch]);

  const handleNavigateToDetail = (workAuthId: string) => {
    navigate(`/hr/work-auth/${workAuthId}`);
  };

  const getStatusBadgeVariant = (status: DocumentReviewStatus) => {
    switch (status) {
      case 'APPROVED': return 'default';
      case 'REJECTED': return 'destructive';
      case 'PENDING': return 'secondary';
      case 'RESUBMISSION_REQUIRED': return 'outline';
    }
  };

  if (documentsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Document Review Queue</h1>
        <p className="text-muted-foreground mt-1">
          Review and approve work authorization documents submitted by managers
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
            <FileText className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {pendingReviewDocuments.length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Documents awaiting review</p>
          </CardContent>
        </Card>
      </div>

      {/* Document List */}
      <Card>
        <CardHeader>
          <CardTitle>Pending Documents</CardTitle>
          <CardDescription>Click on a document to review and approve/reject</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingReviewDocuments.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-muted-foreground">There are no documents pending review</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReviewDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <FileText className="h-10 w-10 text-blue-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{doc.fileName}</p>
                        <Badge variant={getStatusBadgeVariant(doc.reviewStatus)}>
                          {doc.reviewStatus}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Document Type: {doc.documentType}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Uploaded {formatDateTime(doc.submittedAt)} by {doc.submittedBy}
                      </p>
                      {doc.reviewStatus === 'APPROVED' && doc.reviewedAt && (
                        <p className="text-sm text-green-600">
                          Approved {formatDateTime(doc.reviewedAt)}{doc.reviewer ? ` by ${doc.reviewer.firstName} ${doc.reviewer.lastName}` : ''}
                        </p>
                      )}
                      {doc.workAuthorization?.employee && (
                        <p className="text-sm text-muted-foreground">
                          Employee: {doc.workAuthorization.employee.firstName} {doc.workAuthorization.employee.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleNavigateToDetail(doc.workAuthId)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default WorkAuthReviewPage;